// src/pages/WorkspacePage.js

import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  InputAdornment
} from '@mui/material'
import {
  Search as SearchIcon,
  WorkspacePremium as WorkspacePremiumIcon
} from '@mui/icons-material'
import { db } from '../firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate } from 'react-router-dom'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function WorkspacePage() {
  const { t } = useTranslation() // i18n Hook

  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const [workspaces, setWorkspaces] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [filteredWorkspaces, setFilteredWorkspaces] = useState([])
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })

  useEffect(() => {
    if (!currentUser?.uid) return

    const fetchWorkspaces = async () => {
      try {
        // 1) Channels koleksiyonundan kullanıcıya ait olanları al
        const channelsRef = collection(db, 'channels')
        const qChannels = query(channelsRef, where('members', 'array-contains', currentUser.uid))

        const channelSnapshot = await getDocs(qChannels)

        // 2) Channels'dan workspaceId'leri eşsiz olarak al
        const workspaceIds = Array.from(
          new Set(channelSnapshot.docs.map((doc) => doc.data().workspaceId))
        )

        if (workspaceIds.length === 0) {
          setWorkspaces([]) // Kullanıcı hiçbir workspace'e ait değilse boş döndür
          setLoading(false)
          return
        }

        // 3) Workspaces koleksiyonunda bu ID'lere göre sorgu yap
        const workspacesRef = collection(db, 'workspaces')
        const qWorkspaces = query(workspacesRef, where('__name__', 'in', workspaceIds))

        const workspaceSnapshot = await getDocs(qWorkspaces)

        // 4) Workspace verilerini ayıkla
        const wsData = workspaceSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }))

        // Eğer workspace sayısı 1 ise otomatik yönlendir
        if (wsData.length === 1) {
          navigate(`/workspace/${wsData[0].id}`)
          return
        }

        setWorkspaces(wsData)
      } catch (error) {
        console.error('Workspaces alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('workspacePage.fetchError'),
          severity: 'error'
        })
      } finally {
        setLoading(false)
      }
    }

    fetchWorkspaces()
  }, [currentUser, navigate, t])

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredWorkspaces(workspaces)
    } else {
      const filtered = workspaces.filter((ws) =>
        ws.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ws.description?.toLowerCase()?.includes(searchTerm.toLowerCase())
      )
      setFilteredWorkspaces(filtered)
    }
  }, [searchTerm, workspaces])

  // Workspace tıklandığında ChannelsPage'e veya başka bir rotaya yönlenelim
  const handleSelectWorkspace = (workspaceId) => {
    navigate(`/workspace/${workspaceId}`)
  }

  // Snackbar kapatma fonksiyonu
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false })
  }

  if (loading) {
    return (
      <Container sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          {t('workspacePage.loading')}
        </Typography>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Başlık */}
      <Box display="flex" alignItems="center" mb={4}>
        <WorkspacePremiumIcon color="primary" sx={{ mr: 1, fontSize: 40 }} />
        <Typography variant="h4" component="h1">
          {t('workspacePage.pageTitle')}
        </Typography>
      </Box>

      {/* Arama Çubuğu */}
      <Box mb={3}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t('workspacePage.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Workspace Listesi */}
      {workspaces.length === 0 ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" color="textSecondary" align="center">
              {t('workspacePage.noWorkspace')}
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredWorkspaces.map((ws) => (
            <Grid item xs={12} sm={6} md={4} key={ws.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 6,
                  },
                }}
                onClick={() => handleSelectWorkspace(ws.id)}
              >
                <CardHeader
                  title={ws.name}
                  avatar={<WorkspacePremiumIcon color="secondary" />}
                />
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Snackbar Bildirimi */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}